/**
 * Gültiger Wert im Verlauf (Board „Verlauf · Gültige Werte“): ein Wert gilt in einem Zeitabschnitt
 * („seit …“ oder „von – bis“), für einen Aufenthaltstag, als Basis oder Kanalaufschlag. Gültig ab
 * und bis stehen richtig herum; Endgrund, Kanalpreis und Zuordnung bleiben sichtbar.
 */
import type { DynamicPricingExposureHistoryItem } from '@/shared/api';
import type { HistoryDetail } from './historyDetailModel';
import { historyDateTime, historyDay, historyMoney } from './historyFormat';
import { END_REASON, EXPOSURE_KIND, OFFERING, label, type Tone } from './historyText';

export function exposureValidity(item: DynamicPricingExposureHistoryItem) {
  const time = (value: string) => historyDateTime(value, item.scope.timezone);
  return item.validToUtc
    ? `${time(item.validFromUtc)} – ${time(item.validToUtc)}`
    : `seit ${time(item.validFromUtc)}`;
}

export function exposureValue(item: DynamicPricingExposureHistoryItem) {
  if (item.kind === 'ChannelModifier') {
    const percent =
      item.modifierPercent === null
        ? '—'
        : `${item.modifierPercent > 0 ? '+' : ''}${item.modifierPercent} %`;
    return item.channelRate === null
      ? percent
      : `${percent} · ${historyMoney(item.channelRate, item.currency)}`;
  }
  const rate = item.rate === null ? '—' : historyMoney(item.rate, item.currency);
  return item.minimumStay === null ? rate : `${rate} · ${item.minimumStay}`;
}

export function exposureState(item: DynamicPricingExposureHistoryItem): {
  label: string;
  tone: Tone;
} {
  const offering = OFFERING[item.offeringState] ?? {
    label: item.offeringState,
    tone: 'grey' as Tone,
  };
  if (item.validToUtc) {
    return { label: `beendet: ${label(END_REASON, item.endReason ?? '—')}`, tone: 'grey' };
  }
  return { label: `${offering.label} · bestätigt`, tone: offering.tone };
}

export function exposureCells(item: DynamicPricingExposureHistoryItem) {
  return {
    validity: exposureValidity(item),
    apartment: item.scope.propertyName,
    day: historyDay(item.stayDate),
    kind: label(EXPOSURE_KIND, item.kind),
    value: exposureValue(item),
    state: exposureState(item),
  };
}

export function exposureDetail(item: DynamicPricingExposureHistoryItem): HistoryDetail {
  const cells = exposureCells(item);
  const time = (value: string | null) => historyDateTime(value, item.scope.timezone);
  const channel = item.kind === 'ChannelModifier';
  return {
    eyebrow: `Gültiger Wert · ${cells.kind}`,
    title: `${cells.apartment} · ${cells.day}`,
    facts: [
      ['Aufenthaltstag', cells.day],
      ['Gültig ab', time(item.validFromUtc)],
      ['Gültig bis', item.validToUtc ? time(item.validToUtc) : 'gilt noch'],
      ['Art', cells.kind],
      [
        'Angebot',
        label(
          Object.fromEntries(Object.entries(OFFERING).map(([k, v]) => [k, v.label])),
          item.offeringState,
        ),
      ],
      ['Für die Automatik geeignet', item.modelEligible ? 'Ja' : 'Nein'],
      ['Bestätigt durch', 'Rückfrage bei Channex'],
      ['Preis', historyMoney(item.rate, item.currency)],
      ['Mindestnächte', item.minimumStay === null ? '—' : String(item.minimumStay)],
      ['Endgrund', item.endReason ? label(END_REASON, item.endReason) : '—'],
      ...(channel
        ? ([
            ['Kanalaufschlag', exposureValue(item).split(' · ')[0] ?? '—'],
            ['Kanalpreis', historyMoney(item.channelRate, item.currency)],
            ['Kanalverbindung', item.channelConnectionId ?? '—'],
            ['Zuordnung', item.mappingScope.join(', ') || '—'],
          ] as [string, string][])
        : []),
    ],
    refs: [
      ['Gültiger Wert', item.id],
      ['Entscheidung', item.decisionId],
      ['Übertragung', item.publicationId ?? '—'],
      ['Basiswert', item.baseExposureId ?? '—'],
      ['Einstellungs-Version', item.configurationVersionId],
      ['Strategie-Version', item.strategyProfileVersionId ?? '—'],
    ],
  };
}
